import { useContext, useState } from 'react';
import { cloneDeep } from 'es-toolkit/object';

import {
  SalesforceConnection,
  SalesforceConnectionEdition,
  SalesforceConnectionType,
} from '@modules/salesforce/models';

import { getConnectionEnv } from '@modules/salesforce/utils/utils';
import { UserPreferenceContext } from '@contexts/user-preference-context';

import { SalesforceConnectionsService } from '@modules/salesforce/services/index';
import {
  MetadataConnectionModalScreens,
  SelectedSalesForceConnection,
  useSFConnectionState,
} from '@contexts/sf-connection-context';

import { useOAuthFlow } from '@modules/salesforce/shared/hooks/use-oauth-flow';
import { useCleanUpOrphanConnection } from './use-cleanup-orphan-connection';
import { useGlobalState } from '@providers/global-context-provider';

export const useCreateConnection = (isGlobalConnection, isTestConnection) => {
  const [isConnecting, setIsConnecting] = useState<boolean>();
  const [testIsConnecting, setTestIsConnecting] = useState<boolean>();

  const {
    handleUpdateOverlayMode,
    handleUpdateMetadataConnectionModalScreen,
    selectedConnection,
    handleUpdateSelectedConnection,
    handleReload,
    handleConnectionError,
    selectedApplication,
    handleDuplicateConnectionName,
  } = useSFConnectionState();
  const { userPreference } = useContext(UserPreferenceContext);
  const { cleanupOrphanConnection } = useCleanUpOrphanConnection();
  const { handleReloadPrimaryMenu } = useGlobalState();
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const [duplicateConnectionDetails, setDuplicateConnectionDetails] =
    useState(null);

  const handleDuplicateConnection = (connectionDetails) => {
    setDuplicateConnectionDetails(connectionDetails);
    setShowDuplicatePopup(true);
  };

  const projectPreference = userPreference?.preference?.projectPreference;

  const applicationVersionId = isGlobalConnection
    ? null
    : projectPreference?.selectedVersion?.id;

  const handleClose = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    const cleanupDone = await cleanupOrphanConnection(selectedConnection);

    handleUpdateSelectedConnection(null);
    handleUpdateOverlayMode('none');

    if (cleanupDone || selectedConnection?.reload) {
      handleReload();
    }

    if (cleanupDone) {
      handleReloadPrimaryMenu();
    }
  };

  const handleSuccess = (sfConnectionData: SalesforceConnection) => {
    setIsConnecting(false);
    const response: SelectedSalesForceConnection = cloneDeep(sfConnectionData);
    response.salesforceConnectionEdition = getConnectionEnv(sfConnectionData);
    response.reload = true;
    response.hasFailed = false;
    if (response.isDuplicate) {
      handleDuplicateConnectionName(response?.name);
    } else {
      handleUpdateSelectedConnection(response);
    }
    const screenName = response?.isDuplicate
      ? MetadataConnectionModalScreens.METADATA_SYNC_DUPLICATE_SCREEN
      : MetadataConnectionModalScreens.METADATA_SYNC_START_SCREEN;
    handleUpdateMetadataConnectionModalScreen(screenName);
  };

  const handleTestSuccess = (error?: string) => {
    const screenName = MetadataConnectionModalScreens.METADATA_TEST_SUCCESSFUL;
    handleUpdateMetadataConnectionModalScreen(screenName);
    handleConnectionError(error);
  };

  const handleTestFailure = () => {
    const screenName =
      MetadataConnectionModalScreens.CONNECTION_AUTH_FAILURE_SCREEN;
    handleUpdateMetadataConnectionModalScreen(screenName);
  };

  const handleFailure = (
    sfConnectionData: SalesforceConnection,
    error: string,
  ) => {
    setIsConnecting(false);

    const response: SelectedSalesForceConnection = cloneDeep(sfConnectionData);
    response.reload = false;
    response.hasFailed = true;
    handleUpdateSelectedConnection(response);
    handleConnectionError(error);
    handleUpdateMetadataConnectionModalScreen(
      MetadataConnectionModalScreens.CONNECTION_AUTH_FAILURE_SCREEN,
    );
  };

  const handleCreate = (sfConnectionData: SalesforceConnection) => {
    setIsConnecting(true);

    const data = cloneDeep(sfConnectionData);

    data.applicationVersionId = applicationVersionId;
    data.performValidation = true;
    data.connectionType = SalesforceConnectionType.METADATA;
    data.isSandbox =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SANDBOX;
    data.isScratchOrg =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SCRATCH_ORG;
    data.applicationId = isGlobalConnection
      ? selectedApplication?.id
      : projectPreference?.selectedApplicationId;

    handleUpdateSelectedConnection(data);

    if (sfConnectionData.id) {
      SalesforceConnectionsService.update(data)
        .then(handleSuccess)
        .catch((error: string) => {
          handleFailure(data, error);
        })
        .finally(() => {
          setIsConnecting(false);
        });
    } else {
      SalesforceConnectionsService.create(data)
        .then(handleSuccess)
        .catch((error: string) => {
          handleFailure(data, error);
        })
        .finally(() => {
          setIsConnecting(false);
        });
    }
  };

  const handleTestConnection = (sfConnectionData: SalesforceConnection) => {
    setTestIsConnecting(true);

    const data = cloneDeep(sfConnectionData);

    data.applicationVersionId = applicationVersionId;
    data.performValidation = true;
    data.connectionType = SalesforceConnectionType.METADATA;
    data.isSandbox =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SANDBOX;
    data.isScratchOrg =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SCRATCH_ORG;
    data.applicationId = isGlobalConnection
      ? selectedApplication?.id
      : projectPreference?.selectedApplicationId;

    handleUpdateSelectedConnection(data);

    SalesforceConnectionsService.testSFConnection(data)
      .then((response: SalesforceConnection) => {
        if (response?.accessToken) {
          handleTestSuccess();
        } else {
          handleTestFailure();
        }
      })
      .catch((error) => {
        handleTestFailure(error);
      })
      .finally(() => {
        setTestIsConnecting(false);
      });
  };
  const { handleOAuthLogin, isConnecting: isConnectingOAuth } = useOAuthFlow({
    onAuthComplete: !isTestConnection ? handleCreate : handleTestConnection,
    onAuthError: handleFailure,
  });

  const handleUsernamePasswordLogin = (
    sfConnectionData: SalesforceConnection,
  ) => {
    isTestConnection
      ? handleTestConnection(sfConnectionData)
      : handleCreate(sfConnectionData);
  };

  return {
    isConnecting: isConnectingOAuth || isConnecting,
    testIsConnecting,
    hasConnectionFailed: selectedConnection?.hasFailed,
    handleUsernamePasswordLogin,
    handleOAuthLogin,
    handleClose,
    showDuplicatePopup,
    setShowDuplicatePopup,
    duplicateConnectionDetails,
    handleDuplicateConnection,
  };
};
